// src/pages/Admin/VotePage/VotePage.jsx

import React, { useEffect, useMemo, useState } from "react";

import { useUser } from "../../Home/UserContext";

import useVoteActorGroups from "./hooks/useVoteActorGroups";
import useVoteFilters from "./hooks/useVoteFilters";
import useVotesAdmin from "./hooks/useVotesAdmin";

import VoteAdminHeader from "./components/VoteAdminHeader";
import VoteFiltersBar from "./components/VoteFiltersBar";
import VoteFormModal from "./components/VoteFormModal";
import VoteQuestionsModal from "./components/VoteQuestionsModal";
import VoteResultsModal from "./components/VoteResultsModal";
import VoteStatsGrid from "./components/VoteStatsGrid";
import VotesDesktopTable from "./components/VotesDesktopTable";
import VotesMobileList from "./components/VotesMobileList";

import { getVoteAuthDebug, logVoteAuthDebug } from "./utils/voteAuthDebug";

export default function VotePage() {
  const userContext = useUser();

  const authDebug = useMemo(() => getVoteAuthDebug(userContext), [userContext]);

  const { votes, loading, refreshing, deletingId, loadVotes, removeVote } =
    useVotesAdmin();

  const { actorGroups, actorGroupsLoading, loadActorGroups } =
    useVoteActorGroups();

  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    filteredVotes,
    statistics,
    hasActiveFilters,
    resetFilters,
  } = useVoteFilters(votes);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const [selectedVote, setSelectedVote] = useState(null);

  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);

  const [selectedVoteForQuestions, setSelectedVoteForQuestions] =
    useState(null);

  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);

  const [selectedVoteForResults, setSelectedVoteForResults] = useState(null);

  useEffect(() => {
    logVoteAuthDebug("Chargement de VotePage", userContext);
  }, [userContext]);

  const openCreateModal = async () => {
    const currentAuth = logVoteAuthDebug(
      "Ouverture du formulaire de création",
      userContext,
    );

    console.log(
      "[VOTE PAGE] Création autorisée visuellement :",
      currentAuth.isAdmin,
    );

    setSelectedVote(null);
    setIsFormModalOpen(true);

    await loadActorGroups();
  };

  const openEditModal = async (vote) => {
    logVoteAuthDebug("Ouverture du formulaire de modification", userContext);

    setSelectedVote(vote);
    setIsFormModalOpen(true);

    await loadActorGroups();
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setSelectedVote(null);
  };

  const openQuestionsModal = (vote) => {
    setSelectedVoteForQuestions(vote);
    setIsQuestionsModalOpen(true);
  };

  const closeQuestionsModal = () => {
    setIsQuestionsModalOpen(false);
    setSelectedVoteForQuestions(null);
  };

  const openResultsModal = (vote) => {
    setSelectedVoteForResults(vote);
    setIsResultsModalOpen(true);
  };

  const closeResultsModal = () => {
    setIsResultsModalOpen(false);
    setSelectedVoteForResults(null);
  };

  const refreshVoteData = async () => {
    await Promise.all([
      loadVotes({
        silent: true,
      }),

      loadActorGroups({
        force: true,
      }),
    ]);
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-[84px] font-satoshi text-slate-900 dark:bg-slate-950 dark:text-white">
      <VoteAdminHeader />

      <main className="mx-auto max-w-[1380px] space-y-5 px-4 py-5 sm:px-6 sm:py-6">
        <VoteStatsGrid statistics={statistics} />

        <VoteFiltersBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          hasActiveFilters={hasActiveFilters}
          resetFilters={resetFilters}
          refreshing={refreshing}
          onRefresh={refreshVoteData}
          onCreate={openCreateModal}
          resultCount={filteredVotes.length}
        />

        <VotesDesktopTable
          votes={filteredVotes}
          loading={loading}
          deletingId={deletingId}
          actorGroups={actorGroups}
          onEdit={openEditModal}
          onQuestions={openQuestionsModal}
          onResults={openResultsModal}
          onDelete={removeVote}
        />

        <VotesMobileList
          votes={filteredVotes}
          loading={loading}
          deletingId={deletingId}
          actorGroups={actorGroups}
          onEdit={openEditModal}
          onQuestions={openQuestionsModal}
          onResults={openResultsModal}
          onDelete={removeVote}
        />
      </main>

      <VoteFormModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        vote={selectedVote}
        onSaved={refreshVoteData}
        actorGroups={actorGroups}
        actorGroupsLoading={actorGroupsLoading}
        authDebug={authDebug}
        userContext={userContext}
      />

      <VoteQuestionsModal
        isOpen={isQuestionsModalOpen}
        onClose={closeQuestionsModal}
        voteId={selectedVoteForQuestions?.id}
        voteTitle={selectedVoteForQuestions?.title}
      />

      <VoteResultsModal
        isOpen={isResultsModalOpen}
        onClose={closeResultsModal}
        voteId={selectedVoteForResults?.id}
        voteTitle={selectedVoteForResults?.title}
        isAnonymous={selectedVoteForResults?.is_anonymous}
      />
    </div>
  );
}
